import React, { useEffect, useRef } from "react";
import { MicButton } from "../MicButton";
import { useSpeechInput } from "../../../hooks/useSpeechInput";
import { notify } from "../../../services/notify";
import type { VoiceInputButtonProps } from "./VoiceInputButton.types";

/**
 * A mic that fills any text field: start, stop, then the transcript goes to `onText`.
 * Unmounting it (its field closes) aborts the session.
 */
export const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({
  onText,
  provider,
  startLabel,
  stopLabel,
  size,
  className,
}) => {
  const speech = useSpeechInput({ provider });
  const { isListening, transcript, abort, start, stop, unsupportedReason, errorMessage } = speech;

  // When listening ends, what was heard goes to the field, once.
  const wasListening = useRef(false);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;
  useEffect(() => {
    if (wasListening.current && !isListening) {
      const text = transcript.trim();
      if (text) onTextRef.current(text);
      abort();
    }
    wasListening.current = isListening;
  }, [isListening, transcript, abort]);

  useEffect(() => {
    if (errorMessage) notify({ id: "voice-input-error", kind: "warning", title: errorMessage });
  }, [errorMessage]);

  return (
    <MicButton
      isListening={isListening}
      onToggle={() => {
        if (unsupportedReason) return;
        if (isListening) stop();
        else start();
      }}
      disabledReason={unsupportedReason}
      startLabel={startLabel}
      stopLabel={stopLabel}
      size={size}
      className={className}
    />
  );
};

export default VoiceInputButton;
